import React from 'react'
import { SelectCard } from 'src/Components/Ui/SelectCard'
import { TabPanel } from 'src/Components/Ui/TabPanel'
import { Box, Accordion, AccordionDetails, Card } from '@mui/material'
import {
  colorObjs,
  directLogo1Objects,
  directLogo2Objects,
  originalEmbroideryObjects,
  pointColorObjs,
  shadowEdgeColorObjs
} from 'src/features/yellStory/constants/embroidery'
import { State, PartialAction, StateObj, Embroidery } from 'src/types'
import { EmbroideryFormUpDown } from 'src/Components/Domain/EmbroideryFormUpDown'
import { EmbroideryAccordionSummary } from 'src/Components/Domain/EmbroideryAccordionSummary'
import { EmbroideryContent } from 'src/Components/Domain/EmbroideryContent'
import Image from 'next/image'
import {
  useYsrEmbroideriesDispatchGenerator,
  positionObjectsGenerator,
  characterCheckHelper,
  fontImageResolver
} from 'src/features/yellStory/logic/embroiderySetting'
import { generateSubColors } from 'src/logic'
import { embroideryFlagGenerator, selectablePositionGenerator } from 'src/logic'
import { useDispatcher } from 'src/features/yellStory/logic/dispatchGenerator'
import { useLocale } from 'src/locales/useLocale'

type Props = {
  state: State
  selectedIndex: number
  dispatch: React.Dispatch<PartialAction>
}

export const EmbroiderySetter: React.FC<Props> = ({ state, selectedIndex, dispatch }) => {
  const { t } = useLocale()
  const { embroideries, leatherIntegratedMiddle, leatherIntegratedRing, ysrOriginalEmbroidery, ysrDirectLogo1Position, ysrDirectLogo2Position } =
    state
  const { handle } = useYsrEmbroideriesDispatchGenerator(dispatch, state)
  const positionObjects = positionObjectsGenerator(
    leatherIntegratedMiddle.value,
    leatherIntegratedRing.value,
    ysrDirectLogo1Position.value,
    ysrDirectLogo2Position.value
  )
  const handleOriginal = useDispatcher('ysrOriginalEmbroidery', dispatch)
  const handleDirect1Position = useDispatcher('ysrDirectLogo1Position', dispatch)
  const handleDirect1MainColor = useDispatcher('ysrDirectLogo1MainColor', dispatch)
  const handleDirect1EdgeColor = useDispatcher('ysrDirectLogo1EdgeColor', dispatch)
  const handleDirect2Position = useDispatcher('ysrDirectLogo2Position', dispatch)
  const handleDirect2Color = useDispatcher('ysrDirectLogo2Color', dispatch)
  const isSelectedOriginal = ysrOriginalEmbroidery?.value === 'originalEmbroidery'
  const isSelectedLeatherIntegrated = leatherIntegratedMiddle.value !== 'unselected' && leatherIntegratedRing.value !== 'unselected'
  const directLogoPositionFilter = (omitObj: StateObj) => (l: StateObj) => {
    return l.value !== omitObj.value || l.value === 'none'
  }
  const middleFingerFilter = (leatherIntegratedMiddle: StateObj) => (p: StateObj) => {
    return leatherIntegratedMiddle.value !== 'atMiddleFinger' ? p.value !== 'middleFinger' : true
  }
  const ringFingerFilter = (leatherIntegratedMiddle: StateObj) => (p: StateObj) => {
    return leatherIntegratedMiddle.value !== 'atRingFinger' ? p.value !== 'ringFinger' : true
  }
  const embroideriesFilter = (embroideries: Embroidery[]) => (p: StateObj) => {
    return !embroideries.map((e) => e.position.value).includes(p.value)
  }
  const positionFilter = (directLogoPositionFilterTarget: StateObj) => (position: StateObj) => {
    return (
      middleFingerFilter(leatherIntegratedMiddle)(position) &&
      ringFingerFilter(leatherIntegratedRing)(position) &&
      embroideriesFilter(embroideries)(position) &&
      directLogoPositionFilter(directLogoPositionFilterTarget)(position)
    )
  }
  const shouldOmitEdgeColor = (mainColor: StateObj) => (edgeColor: StateObj) => {
    return mainColor.value === 'gold' ? !['silver', 'gold'].includes(edgeColor.value) : true
  }
  const shouldOmitMainColor = (edgeColor: StateObj) => (mainColor: StateObj) => {
    return ['silver', 'gold'].includes(edgeColor.value) ? !['silver', 'gold'].includes(mainColor.value) : true
  }
  return (
    <TabPanel selectedIndex={selectedIndex} index={2}>
      <SelectCard
        summary={t.ORIGINAL_EMBROIDERY}
        selectedLabel={ysrOriginalEmbroidery?.label}
        objects={originalEmbroideryObjects}
        isError={ysrOriginalEmbroidery?.value === 'unselected'}
        handleChange={handleOriginal}
        caution={
          isSelectedOriginal
            ? 'データ代￥11,000 + 刺繍代¥5,500(税込)'
            : 'PDFまたはAIデータが必須になります。手書きなどは対応できかねますのでご了承ください。'
        }
        description={isSelectedOriginal && '刺繍内容の詳細はお近くの小売店様にお伝えください。'}
      />
      <SelectCard
        summary={t.DIRECT_LOGO_EMBROIDERY_1}
        selectedLabel={ysrDirectLogo1Position?.label}
        objects={directLogo1Objects.filter(positionFilter(ysrDirectLogo2Position))}
        isError={ysrDirectLogo1Position?.value === 'unselected'}
        handleChange={handleDirect1Position}
        caution={'ゴールドxゴールドの配色はできません。'}
        isDisplay={isSelectedLeatherIntegrated}
      />
      <SelectCard
        summary={t.DIRECT_LOGO_EMBROIDERY_1_MAIN_COLOR}
        selectedLabel={state.ysrDirectLogo1MainColor?.label}
        objects={colorObjs.filter(shouldOmitMainColor(state.ysrDirectLogo1EdgeColor))}
        isError={state.ysrDirectLogo1MainColor?.value === 'unselected'}
        handleChange={handleDirect1MainColor}
        isDisplay={!['none', 'unselected'].includes(ysrDirectLogo1Position?.value)}
      />
      <SelectCard
        summary={t.DIRECT_LOGO_EMBROIDERY_1_EDGE_COLOR}
        selectedLabel={state.ysrDirectLogo1EdgeColor?.label}
        objects={colorObjs.filter(shouldOmitEdgeColor(state.ysrDirectLogo1MainColor))}
        isError={state.ysrDirectLogo1EdgeColor.value === 'unselected'}
        handleChange={handleDirect1EdgeColor}
        isDisplay={!['none', 'unselected'].includes(ysrDirectLogo1Position?.value)}
      />
      <SelectCard
        summary={t.DIRECT_LOGO_EMBROIDERY_2}
        selectedLabel={ysrDirectLogo2Position?.label}
        objects={directLogo2Objects.filter(positionFilter(ysrDirectLogo1Position))}
        isError={ysrDirectLogo2Position?.value === 'unselected'}
        handleChange={handleDirect2Position}
        isDisplay={!['unselected'].includes(ysrDirectLogo1Position?.value)}
      />
      <SelectCard
        summary={t.DIRECT_LOGO_EMBROIDERY_2_COLOR}
        selectedLabel={state.ysrDirectLogo2Color?.label}
        objects={colorObjs}
        isError={state.ysrDirectLogo2Color?.value === 'unselected'}
        handleChange={handleDirect2Color}
        isDisplay={!['none', 'unselected'].includes(ysrDirectLogo2Position?.value)}
      />

      {isSelectedLeatherIntegrated ? (
        <>
          {ysrDirectLogo2Position.value === 'unselected' ? (
            <Box fontSize={'13px'} color={'blue'} pb={1}>
              [ダイレクトロゴ刺繍1,2]を先に選択してください。
            </Box>
          ) : (
            <>
              <EmbroideryFormUpDown {...{ embroideries, positionObjects, dispatch }} />
              {embroideries.map((embroidery, i) => {
                const {
                  shadowColorLabel,
                  edgeColorLabel,
                  pointColorLabel,
                  disabledShadowColor,
                  disabledEdgeColor,
                  isArribaFont,
                  isSelectedTypeFace
                } = embroideryFlagGenerator(embroidery)
                const { contentMaxLength, existsContent, typeFaceObjects } = characterCheckHelper(embroidery.content)
                const selectablePosition = selectablePositionGenerator(
                  embroideries,
                  positionObjects,
                  [],
                  ysrDirectLogo2Position.value === 'leatherLiningFirst',
                  i
                )
                const { typeFace, typeDecoration, combination } = fontImageResolver(embroidery)
                const shadowColors = generateSubColors(disabledShadowColor, shadowEdgeColorObjs)
                const edgeColors = generateSubColors(disabledEdgeColor, shadowEdgeColorObjs)
                return (
                  <Box key={`${embroidery.id}-${i}`} my={1}>
                    <Accordion>
                      <EmbroideryAccordionSummary embroideryIndex={i} />
                      <AccordionDetails style={{ flexWrap: 'wrap' }}>
                        <Box width={'100%'}>
                          <SelectCard
                            summary={t.POSITION}
                            selectedLabel={embroidery.position?.label}
                            objects={selectablePosition}
                            index={i}
                            handleChange={handle.position}
                            disabled={existsContent}
                            caution={existsContent && '刺繍位置を変更するには、刺繍内容をリセットしてください'}
                          />
                        </Box>
                        <Box width={'100%'}>
                          <EmbroideryContent
                            content={embroidery.content}
                            contentMaxLength={contentMaxLength}
                            embroideryIndex={i}
                            handleContent={handle.content}
                          />
                        </Box>
                        <Box width={'100%'} mt={2}>
                          <SelectCard
                            summary={t.TYPE_FACE}
                            selectedLabel={embroidery.typeFace?.label}
                            objects={typeFaceObjects}
                            index={i}
                            handleChange={handle.typeFace}
                            disabled={!existsContent}
                            description={'書体は一部、実際の書体と異なります。カタログを併せてご確認ください'}
                          />
                        </Box>
                        <Box my={1} width={'100%'}>
                          <Card>
                            <Box fontWeight={'bold'}>{combination}</Box>
                            <Box>
                              <Image
                                src={`/yellstory/embroidery/${typeFace.path}/${typeDecoration.path}.jpg`}
                                alt={combination}
                                width={196}
                                height={77}
                              />
                            </Box>
                          </Card>
                        </Box>
                        <Box width={'100%'}>
                          <SelectCard
                            summary={t.MAIN_COLOR}
                            selectedLabel={embroidery.color?.label}
                            selectedColor={embroidery.color?.color}
                            objects={colorObjs}
                            isError={embroidery.color?.value === 'unselected'}
                            index={i}
                            handleChange={handle.color}
                            disabled={!isSelectedTypeFace}
                            caution={!isSelectedTypeFace && '書体を選択してください'}
                          />
                        </Box>
                        <Box width={'100%'}>
                          <SelectCard
                            summary={t.POINT_COLOR}
                            selectedLabel={pointColorLabel}
                            selectedColor={embroidery.pointColor?.color}
                            objects={pointColorObjs}
                            isError={embroidery.pointColor?.value === 'unselected'}
                            defaultExpanded={false}
                            index={i}
                            handleChange={handle.pointColor}
                            isDisplay={isArribaFont}
                            caution={'※シミュレーションには反映されません'}
                          />
                        </Box>
                        <Box width={'100%'}>
                          <SelectCard
                            summary={t.SHADOW_COLOR}
                            selectedLabel={shadowColorLabel}
                            selectedColor={embroidery.shadowColor?.color}
                            objects={shadowColors}
                            isError={embroidery.shadowColor?.value === 'unselected'}
                            defaultExpanded={false}
                            index={i}
                            handleChange={handle.shadowColor}
                          />
                        </Box>
                        <Box width={'100%'}>
                          <SelectCard
                            summary={t.EDGE_COLOR}
                            selectedLabel={edgeColorLabel}
                            selectedColor={embroidery.edgeColor?.color}
                            objects={edgeColors}
                            isError={embroidery.edgeColor?.value === 'unselected'}
                            defaultExpanded={false}
                            index={i}
                            handleChange={handle.edgeColor}
                          />
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )
              })}
            </>
          )}
        </>
      ) : (
        <Box fontSize={'13px'} color={'blue'} pb={1}>
          [パーツ設定/革一体形状]を先に選択してください。
        </Box>
      )}
    </TabPanel>
  )
}
